import { useQuery } from '@apollo/client';
import { colors, Loading, ModalHeader, Stack } from '@ui-components/neo.tax';

import { GET_FINCH_SESSION } from '@neo.tax/shared/graphql';
import { useCurrentCompanyId } from '@neo.tax/shared/hooks';
import { FinchProvider, getFinchProviderKey } from '@neo.tax/shared/third-parties';

import { AlertCircle, StyledButton } from './connect-finch.styled';
import { ConnectProvider } from './ConnectProvider';
import { useSyncFinchProvider } from './hooks';

interface ConnectFinchProps {
  providerName: FinchProvider;
  close: () => void;
  restart: () => void;
  fiscalYearId: string;
}

interface ConnectFinchWithSessionProps extends ConnectFinchProps {
  finchSessionId: string;
}

export const ConnectFinch = ({ close, fiscalYearId, providerName, restart }: ConnectFinchProps): React.ReactElement => {
  const companyId = useCurrentCompanyId();
  const { data, error, loading } = useQuery(GET_FINCH_SESSION, {
    variables: {
      companyId,
      provider: getFinchProviderKey(providerName),
    },
    fetchPolicy: 'network-only',
  });

  if (loading) {
    return <Loading height={240} />;
  }

  if (error) {
    return (
      <Stack direction="column" space={6}>
        <ModalHeader
          copy="An error occurred while connecting to the selected provider. Please try again."
          icon={<AlertCircle color={colors.rsbry[600]} />}
          iconBgColor={colors.white}
          title="Connection Error"
        />
        <StyledButton variant="white" rounded="regular" size="base" onClick={restart}>
          Restart
        </StyledButton>
      </Stack>
    );
  }

  return (
    <ConnectFinchWithSession
      fiscalYearId={fiscalYearId}
      providerName={providerName}
      finchSessionId={data?.getFinchSession.sessionId}
      close={close}
      restart={restart}
    />
  );
};

const ConnectFinchWithSession = ({
  close,
  finchSessionId,
  fiscalYearId,
  providerName,
  restart,
}: ConnectFinchWithSessionProps): React.ReactElement => {
  const { connect, connectStatus } = useSyncFinchProvider({ finchSessionId, fiscalYearId, providerName });
  return (
    <ConnectProvider
      close={close}
      restart={restart}
      connect={connect}
      connectStatus={connectStatus}
      provider={{ providerName, providerType: 'payroll' }}
    />
  );
};
